import { z } from 'zod';

import { publicUserSchema } from './user';

/**
 * Comments are one level deep (spec §4.1), so the recursion is bounded: a
 * top-level comment carries replies, a reply carries none. Modelling that
 * explicitly rather than with `z.lazy` keeps the inferred type flat and honest
 * about the shape the API actually returns.
 */
const commentBase = z.object({
  id: z.string(),
  postId: z.string(),
  parentId: z.string().nullable(),
  body: z.string(),
  author: publicUserSchema.nullable(),
  upvoteCount: z.number(),
  replyCount: z.number(),
  hasUpvoted: z.boolean(),
  canEdit: z.boolean(),
  canDelete: z.boolean(),
  isDeleted: z.boolean(),
  isEdited: z.boolean(),
  createdAt: z.string(),
  hasMoreReplies: z.boolean(),
});

export const replySchema = commentBase.extend({ replies: z.array(z.never()).default([]) });

export const commentSchema = commentBase.extend({
  replies: z.array(replySchema.omit({ replies: true })),
});

export const commentListSchema = z.array(commentSchema.omit({ replies: true }));

export const commentPageSchema = z.object({
  items: z.array(commentSchema),
  nextCursor: z.string().nullable(),
  hasMore: z.boolean(),
  totalCount: z.number(),
});

/** A comment shown away from its thread — on a profile (spec §9.4). */
export const authoredCommentSchema = commentBase.extend({
  post: z.object({
    id: z.string(),
    type: z.enum(['GOOD', 'BAD', 'IDEAL']),
    title: z.string().nullable(),
    excerpt: z.string(),
  }),
});

export const authoredCommentPageSchema = z.object({
  items: z.array(authoredCommentSchema),
  nextCursor: z.string().nullable(),
  hasMore: z.boolean(),
  totalCount: z.number(),
});

export const voteResultSchema = z.object({
  upvoteCount: z.number(),
  hasUpvoted: z.boolean(),
});

export const bookmarkResultSchema = z.object({
  hasBookmarked: z.boolean(),
});

export type Comment = z.infer<typeof commentSchema>;
export type Reply = z.infer<typeof commentSchema>['replies'][number];
export type CommentPage = z.infer<typeof commentPageSchema>;
export type VoteResult = z.infer<typeof voteResultSchema>;

export type AuthoredComment = z.infer<typeof authoredCommentSchema>;
export type AuthoredCommentPage = z.infer<typeof authoredCommentPageSchema>;
